import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Switch } from '../../components/ui/Switch';
import { Badge } from '../../components/ui/Badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/Table';
import { Skeleton } from '../../components/ui/Skeleton';
import { Shield, Save, RefreshCw, Building2, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useGlobalPolicy,
  useUpdateGlobalPolicy,
  useTenantsWithOverrides,
  GlobalPolicy,
} from '../../hooks/useFinancialSecurity';

export function FinancialSecuritySettings() {
  const { data: globalPolicy, isLoading: loadingPolicy } = useGlobalPolicy();
  const { data: tenantsWithOverrides, isLoading: loadingTenants } = useTenantsWithOverrides();
  const updatePolicy = useUpdateGlobalPolicy();

  const [formData, setFormData] = useState<Partial<GlobalPolicy>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form when data loads
  if (globalPolicy && Object.keys(formData).length === 0) {
    setFormData(globalPolicy);
  }

  const handleChange = (key: keyof GlobalPolicy, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await updatePolicy.mutateAsync(formData);
      toast.success('Políticas de segurança atualizadas com sucesso');
      setHasChanges(false);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao atualizar políticas');
    }
  };

  const handleReset = () => {
    if (globalPolicy) {
      setFormData(globalPolicy);
      setHasChanges(false);
    }
  };

  if (loadingPolicy) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Segurança Financeira</h1>
            <p className="text-muted-foreground">
              Configurações globais de segurança para tokenização
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Descartar
            </Button>
          )}
          <Button onClick={handleSave} disabled={!hasChanges || updatePolicy.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {updatePolicy.isPending ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Coverage Ratio */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Taxa de Cobertura Mínima</CardTitle>
            <CardDescription>
              Percentual mínimo de cobertura de custódia exigido para operações
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                min={0}
                max={200}
                step={1}
                value={formData.minCoverageRatioThreshold ?? 100}
                onChange={(e) => handleChange('minCoverageRatioThreshold', Number(e.target.value))}
                className="w-24"
              />
              <span className="text-muted-foreground">%</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Recomendado: 100% (cobertura total)
            </p>
          </CardContent>
        </Card>

        {/* Tenant Override */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Customização por Tenant</CardTitle>
            <CardDescription>
              Permitir que tenants customizem suas próprias políticas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <Label htmlFor="allowTenantOverride">Permitir Override</Label>
              <Switch
                id="allowTenantOverride"
                checked={formData.allowTenantOverride ?? true}
                onCheckedChange={(checked) => handleChange('allowTenantOverride', checked)}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Tenants só podem ser mais restritivos que a política global
            </p>
          </CardContent>
        </Card>

        {/* Custody Validations */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Validações de Custódia</CardTitle>
            <CardDescription>
              Exigir custódia ativa para operações de token
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="requireCustodyForDeploy">Deploy de Token</Label>
                <p className="text-xs text-muted-foreground">
                  Exigir custódia ACTIVE antes do deploy
                </p>
              </div>
              <Switch
                id="requireCustodyForDeploy"
                checked={formData.requireCustodyForDeploy ?? true}
                onCheckedChange={(checked) => handleChange('requireCustodyForDeploy', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="requireCustodyForMint">Mint de Tokens</Label>
                <p className="text-xs text-muted-foreground">
                  Exigir custódia ACTIVE e cobertura suficiente
                </p>
              </div>
              <Switch
                id="requireCustodyForMint"
                checked={formData.requireCustodyForMint ?? true}
                onCheckedChange={(checked) => handleChange('requireCustodyForMint', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Automatic Updates */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Atualizações Automáticas</CardTitle>
            <CardDescription>
              Configurações de atualização automática de dados
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="autoUpdatePoR">Proof of Reserve</Label>
                <p className="text-xs text-muted-foreground">
                  Atualizar PoR após mint/burn
                </p>
              </div>
              <Switch
                id="autoUpdatePoR"
                checked={formData.autoUpdatePoROnSupplyChange ?? true}
                onCheckedChange={(checked) => handleChange('autoUpdatePoROnSupplyChange', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="requireLedger">Integração com Ledger</Label>
                <p className="text-xs text-muted-foreground">
                  Exigir crédito via ledger para yields
                </p>
              </div>
              <Switch
                id="requireLedger"
                checked={formData.requireLedgerIntegrationForYield ?? true}
                onCheckedChange={(checked) => handleChange('requireLedgerIntegrationForYield', checked)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tenants with Overrides */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            <CardTitle className="text-lg">Tenants com Customizações</CardTitle>
          </div>
          <CardDescription>
            Tenants que possuem políticas customizadas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingTenants ? (
            <Skeleton className="h-32 w-full" />
          ) : tenantsWithOverrides && tenantsWithOverrides.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Customizações</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenantsWithOverrides.map((tenant) => (
                  <TableRow key={tenant.tenantId}>
                    <TableCell className="font-medium">{tenant.tenantName}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {tenant.customizations.map((c) => (
                          <Badge key={c} variant="secondary" className="text-xs">
                            {c}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm">
                        <Settings2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum tenant possui customizações</p>
              <p className="text-sm">Todos estão usando as políticas globais</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default FinancialSecuritySettings;
